/* eslint-disable react/button-has-type */
import type { Dispatch, PropsWithChildren, ReactNode, SetStateAction } from "react";
import { Fragment, useCallback, useEffect, useState } from "react";
import type { ReadonlyNonEmptyArray } from "fp-ts/lib/ReadonlyNonEmptyArray";
import { useStableO } from "fp-ts-react-stable-hooks";

import type { BLConfigWithLog } from "@scripts/bondlink";
import { Static } from "@scripts/bondlinkStatic";
import type { LinkableU, PageLink } from "@scripts/codecs/linkable";
import { constVoid, E, O, pipe, RNEA } from "@scripts/fp-ts";
import { exitDisclaimer } from "@scripts/generated/domaintables/featureFlags";
import { relatedIssuers as relatedIssuersPage } from "@scripts/generated/domaintables/pages";
import type { Issuer } from "@scripts/generated/models/issuer";
import type { RelatedIssuerWithIssuer } from "@scripts/generated/models/relatedIssuer";
import type { WithStatusU } from "@scripts/generated/models/threadThrough";
import type { User, UserWithRoles } from "@scripts/generated/models/user";
import * as V2Router from "@scripts/generated/routers/v2Router";
import { relatedIssuersMeta } from "@scripts/meta/dataMeta";
import { Anchor, AnchorWithChildren } from "@scripts/react/components/Anchor";
import { mapOrEmpty } from "@scripts/react/components/Empty";
import { type SelectOption, SelectRaw } from "@scripts/react/components/form/Select";
import { NotificationPortal } from "@scripts/react/components/layout/Notifications";
import { Svg } from "@scripts/react/components/Svg";
import { Tooltip } from "@scripts/react/components/Tooltip";
import { useConfig } from "@scripts/react/context/Config";
import type { KlassOutput } from "@scripts/react/util/klassOutput";
import { useElementSize } from "@scripts/react/util/useElementSize";
import { useModal } from "@scripts/react/util/useModal";
import { useScrollPositionEffect } from "@scripts/react/util/useScrollPosition";
import { getEditableTitleOrDefault } from "@scripts/routes/routing/ssr";
import type { UrlInterface } from "@scripts/routes/urlInterface";
import { isFFEnabled } from "@scripts/syntax/featureFlags";
import { issuerHomeUrl } from "@scripts/syntax/issuer";
import { toAnchor } from "@scripts/syntax/linkable";
import { isServerSide } from "@scripts/util/ssr";
import { absoluteUrl } from "@scripts/util/url";

import { SitesAlerts } from "@scripts-ssr/components/Alert";
import { BondlinkLogoForNav, IssuerLogo } from "@scripts-ssr/components/IssuerLogo";

import menuIcon from "@svgs/menu.svg";

import { useIssuerSitesDispatch, useIssuerSitesSelector } from "../state/store";
import type { KlassProp } from "../util/classnames";
import { klass, klassConditional, klassProp } from "../util/classnames";
import { ExitDisclaimerModal } from "./disclaimer/exitDisclaimerModal";

const NavLists = (props: {
  containerKlass: KlassProp;
  closeNav: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  openNav: () => void;
}) => {
  const config = useConfig();
  const relatedIssuers = useIssuerSitesSelector("relatedIssuers");
  const navLinks: ReadonlyNonEmptyArray<[PageLink, ReadonlyArray<KlassOutput>, ReadonlyArray<LinkableU>]> =
    useIssuerSitesSelector("navLinks");

  return (
    <div {...klass("nav-lists-container", props.containerKlass)}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
    >
      <div {...klass("nav", "nav-lists")}>
        <div {...klass("container", "nav-menu", klassConditional("related")(relatedIssuers.length > 0))}>
          <nav {...klass("navigation")}>
            {navLinks.map(([pageLink, machineName, links]) =>
              <div key={pageLink.url} {...klass(machineName)}>
                {toAnchor(config)(pageLink, { klass: klassProp("nav-link-primary") })}
                <ul>
                  {links.map((linkable) =>
                    <li
                      key={linkable.url}
                      {...klass("nav-link-secondary")}
                      onFocus={props.openNav}
                      onBlur={props.closeNav}
                    >{toAnchor(config)(linkable, { klass: klassProp("nav-link-secondary") })}</li>
                  )}
                </ul>
              </div>
            )}
          </nav>
        </div>
      </div>
    </div>
  );
};

export const DirectIssuerMainNav = () => {
  const config = useConfig();
  const issuer = useIssuerSitesSelector("issuer");
  const flash = useIssuerSitesSelector("flash");
  const notifications = useIssuerSitesSelector("notifications");
  const dispatch = useIssuerSitesDispatch();
  const navLinks: ReadonlyNonEmptyArray<[PageLink, ReadonlyArray<KlassOutput>, ReadonlyArray<LinkableU>]> = useIssuerSitesSelector("navLinks");
  const userWithRolesO = useIssuerSitesSelector("user");
  const userO: O.Option<User> = O.map((userWithRoles: UserWithRoles) => userWithRoles.user)(userWithRolesO);
  const loginParams = {
    bankId: O.none,
    issuerId: O.some(issuer.id),
    uhash: O.none,
    reason: O.none,
    redirect: O.none,
  };
  const loginUrl = absoluteUrl(config)(V2Router.baseAuthControllerLogin(loginParams));
  const signupUrl = absoluteUrl(config)(V2Router.baseAuthControllerSignup(loginParams));
  const logoutUrl = V2Router.baseAuthControllerLogout();
  const isBlpIssuer = O.isSome(issuer.bankId);

  const [navIsOpen, setNavIsOpen] = useState(false);
  const [hoverNavHeaders, setHoverNavHeaders] = useState(false);
  const [hoverNavListsContainer, setHoverNavListsContainer] = useState(false);
  const [blBarElement, blBarSize] = useElementSize();
  const [origBlBarHeight, setOrigBlBarHeight] = useState(blBarSize.height);
  const [mainNavStuck, setMainNavStuck] = useState(false);
  const [isPreSticky, setIsPreSticky] = useState<boolean>(false);

  const toggleNav = () => {
    setNavIsOpen(!navIsOpen);
  };

  const openNav = useCallback(() => {
    if (!navIsOpen) {
      setNavIsOpen(true);
    }
  }, [navIsOpen]);

  const closeNav = useCallback(() => {
    if (navIsOpen) {
      setNavIsOpen(false);
    }
  }, [navIsOpen]);

  useEffect(() => {
    if (hoverNavHeaders || hoverNavListsContainer) {
      openNav();
    } else {
      closeNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoverNavHeaders, hoverNavListsContainer]);

  useEffect(() => {
    setOrigBlBarHeight(blBarSize.height);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blBarSize.width]);

  const scrollHandler = useCallback((scrollPos: number) => {
    if (scrollPos > origBlBarHeight && !mainNavStuck) {
      stick(setMainNavStuck, setIsPreSticky, navIsOpen, setNavIsOpen);
    } else if (scrollPos < origBlBarHeight && mainNavStuck) {
      unstick(setMainNavStuck, setIsPreSticky);
    }
  }, [origBlBarHeight, mainNavStuck, navIsOpen]);

  useScrollPositionEffect(scrollHandler, 0);

  return <header {...klass("page-header")}>
    <NotificationPortal notifications={notifications.notifications} dispatch={dispatch} />
    <SitesAlerts flash={flash} dispatch={dispatch} />
    <div
      {...klass(
        "main-nav",
        klassConditional("open")(navIsOpen),
        klassConditional("pre-sticky")(isPreSticky),
        klassConditional("sticky")(mainNavStuck),
      )}
    >
      <div {...klass("bondlink-bar", "issuer-bondlink-bar", "container")} ref={blBarElement}>
        <LogoContainer />
        <div {...klass("sticky-headers")}>
          <h2 {...klass("headers-primary")}>
            <div>
              <div {...klass("issuer-name")}>{issuer.name}</div>
            </div>
          </h2>
          {isBlpIssuer ? null : <nav {...klass("navigation")}>
            {navLinks.map(([pageLink, machineName]) =>
              <div key={pageLink.url} {...klass(machineName)}>
                {toAnchor(config)(pageLink, { klass: klassProp("nav-link-primary") })}
              </div>
            )}
          </nav>}
        </div>
        <div {...klass("actions", klassConditional("pb-0")(isBlpIssuer))}>
          {isBlpIssuer ? null : <div {...klass("home-link", "mr-05", "mr-md-0")}><BondlinkLogoForNav /></div>}
          <div {...klass("account-links")}>
            <LoginLogoutButtons loginUrl={loginUrl} signupUrl={signupUrl} logoutUrl={logoutUrl} user={userO} />
          </div>
        </div>
        <div {...klass("menu-mobile", "inverted")} onClick={toggleNav}>
          <button {...klass("btn-link", "menu-mobile-container")} onClick={toggleNav}>
            <span {...klass("menu-mobile-text")}>Menu</span>
            <Svg src={menuIcon} />
          </button>
        </div>
      </div>
      {isBlpIssuer ? null : <>
        <div {...klass("nav", "nav-headers", "inverted")}>
          <div
            {...klass("container", "nav-menu")}
            onMouseEnter={() => setHoverNavHeaders(true)}
            onMouseLeave={() => setHoverNavHeaders(false)}
          >
            <nav {...klass("navigation")}>
              {navLinks.map(([pageLink, machineName]) =>
                <div {...klass(machineName)} key={pageLink.url}>
                  {toAnchor(config)(pageLink, { klass: klassProp("nav-link-primary") })}
                </div>
              )}
            </nav>
          </div>
        </div>
        <NavLists
          containerKlass="d-none-until-lg"
          onMouseEnter={() => setHoverNavListsContainer(true)}
          onMouseLeave={() => setHoverNavListsContainer(false)}
          openNav={openNav}
          closeNav={closeNav}
        />
        <NavLists
          containerKlass="d-lg-none"
          onMouseEnter={constVoid}
          onMouseLeave={constVoid}
          openNav={openNav}
          closeNav={closeNav}
        />
      </>}
    </div>
  </header>;
};

const stick = (
  setIsStuck: Dispatch<SetStateAction<boolean>>,
  setIsPreSticky: Dispatch<SetStateAction<boolean>>,
  navIsOpen: boolean,
  setNavOpen: Dispatch<SetStateAction<boolean>>
) => {
  const _stick = () => {
    globalThis.setTimeout(() => {
      setIsPreSticky(false);
      setIsStuck(true);
    }, Static.baseTransitionDelay);
  };

  setIsPreSticky(true);

  if (navIsOpen) {
    globalThis.setTimeout(() => {
      setNavOpen(false);
      _stick();
    }, Static.baseTransitionDelay);
  } else {
    _stick();
  }
};

const unstick = (
  setIsStuck: Dispatch<SetStateAction<boolean>>,
  setIsPreSticky: Dispatch<SetStateAction<boolean>>,
) => {
  setIsPreSticky(true);

  globalThis.setTimeout(() => {
    setIsPreSticky(false);
    setIsStuck(false);
  }, Static.baseTransitionDelay);
};


const LogoutButton = (props: { logoutUrl: UrlInterface<"GET"> }) =>
  <Anchor
    target="_self"
    route={{
      title: "Log Out",
      route: props.logoutUrl,
    }}
    klass={klassProp("account-link", "log-out")}
  />;

const LoginButton = (props: { loginUrl: UrlInterface<"GET"> }) =>
  <AnchorWithChildren
    target="_self"
    route={{
      title: "Login",
      route: props.loginUrl,
    }}
    klass={klassProp("account-link", "font-sans-normal-700")}
  >
    My BondLink
  </AnchorWithChildren>;

const CreateAccountAndLoginButtons = (props: { loginUrl: UrlInterface<"GET">, signupUrl: UrlInterface<"GET"> }) => <>
  <Anchor
    target="_self"
    route={{
      title: "Create Account",
      route: props.signupUrl,
    }}
    klass={klassProp("account-link", "font-sans-normal-700")}
  />
  <Anchor
    target="_self"
    route={{
      title: "Login",
      route: props.loginUrl,
    }}
    klass={klassProp("account-link")}
  />
</>;

const LoginLogoutButtons = (props: {
  user: O.Option<User>;
  loginUrl: UrlInterface<"GET">;
  signupUrl: UrlInterface<"GET">;
  logoutUrl: UrlInterface<"GET">;
}) => O.isSome(props.user)
    ? (
      <>
        <LoginButton loginUrl={props.loginUrl} />
        <LogoutButton logoutUrl={props.logoutUrl} />
      </>
    ) : (
      <CreateAccountAndLoginButtons
        loginUrl={props.loginUrl}
        signupUrl={props.signupUrl}
      />
    );

const LogoContainer = () => {
  const issuer = useIssuerSitesSelector("issuer");
  const relatedIssuers = useIssuerSitesSelector("relatedIssuers");
  const prefs = useIssuerSitesSelector("prefs");
  const primaryOfficer = useIssuerSitesSelector("primaryOfficer");

  return <div {...klass("issuer-logo-container")}>
    <IssuerLogo issuer={issuer} />
    {pipe(
      relatedIssuers,
      RNEA.fromReadonlyArray,
      mapOrEmpty((relIssuers) => <RelatedIssuersDropdown relatedIssuers={relIssuers} />),
    )}
    <div {...klass("primary-officer")}>
      {pipe(
        prefs,
        O.chain(_ => _.customHeaderText1),
        O.fold(
          () => pipe(
            primaryOfficer,
            O.filter(_ => _.data.record.officerPageVisibility),
            mapOrEmpty(_ => <>
              <span {...klass("primary-officer-name")}>{_.data.record.firstName} {_.data.record.lastName},</span>
              {" "}
              <span>{_.data.record.title}</span>
            </>)
          ),
          _ => <span {...klass("primary-officer-name")}>{_}</span>
        ),
      )}
    </div>
  </div>;
};

// If the related issuer has a BondLink site, we'll link to it if either of the following is true:
//   - The related issuer is published
//   - The current issuer is not published
// If the related issuer does not have a BondLink site, we'll link to the `outsideLink` if given.
const relatedIssuerLink = (
  config: BLConfigWithLog,
  currentIssuer: Issuer,
  relatedIssuer: WithStatusU<RelatedIssuerWithIssuer>,
): O.Option<string> =>
  O.fold(
    () => relatedIssuer.data.record.related.outsideLink,
    (issuer: Issuer) => O.fromPredicate(() => issuer.published || !currentIssuer.published)(issuerHomeUrl(config)(issuer).url),
  )(relatedIssuer.data.record.issuer);

type RelatedIssuersDropdownProps = {
  relatedIssuers: RNEA.ReadonlyNonEmptyArray<WithStatusU<RelatedIssuerWithIssuer>>;
};

const RelatedIssuersDropdown = (props: RelatedIssuersDropdownProps) => {
  const config = useConfig();
  const currentIssuer = useIssuerSitesSelector("issuer");
  const iffs = useIssuerSitesSelector("iffs");
  const exitDisclaimerEnabled = isFFEnabled(exitDisclaimer)(iffs);
  const pages = useIssuerSitesSelector("pages");
  const relatedIssuersTitle = getEditableTitleOrDefault(relatedIssuersPage, relatedIssuersMeta)(pages);
  const [modalOpen, openModal, closeModal] = useModal("Exit Disclaimer Modal");
  const [exitDisclaimerLink, setExitDisclaimerLink] = useStableO<string>(O.none);

  const options: ReadonlyArray<SelectOption<WithStatusU<RelatedIssuerWithIssuer>>> = props.relatedIssuers.map(relatedIssuer => ({
    label: relatedIssuer.data.record.related.name,
    value: relatedIssuer,
    disabled: O.isNone(relatedIssuerLink(config, currentIssuer, relatedIssuer)),
    formatLabelOrValueOrAll: E.right(_ => <Fragment key={_.value.data.id}>
      {O.fold(
        (): ReactNode => <RelatedIssuerDisabledTooltip relatedIssuerName={_.label}><div>{_.label}</div></RelatedIssuerDisabledTooltip>,
        () => _.label,
      )(relatedIssuerLink(config, currentIssuer, _.value))}
    </Fragment>),
  }));

  return <div {...klass("related-issuers-filter")}>
    <SelectRaw
      placeholderComponent={() => relatedIssuersTitle}
      labelOrAriaLabel={E.right(relatedIssuersTitle)}
      value={O.none}
      options={options}
      onChange={_ => {
        pipe(
          relatedIssuerLink(config, currentIssuer, _.value),
          O.fold(
            constVoid,
            link => {
              if (exitDisclaimerEnabled) {
                setExitDisclaimerLink(O.some(link));
                openModal();
              } else {
                window.open(link, "_blank");
              }
            }
          )
        );
      }}
      isSearchable={false}
      placeholder={O.none}
      error={O.none}
      required={true}
    />
    {pipe(exitDisclaimerLink, O.filter(() => exitDisclaimerEnabled), mapOrEmpty(link =>
      <ExitDisclaimerModal
        modalOpen={modalOpen}
        dismissAction={closeModal}
        outsideLink={link}
      />
    ))}
  </div>;
};

const RelatedIssuerDisabledTooltip = (props: PropsWithChildren<{
  relatedIssuerName: string;
}>) =>
  <Tooltip
    appendTo={isServerSide() ? "parent" : document.body}
    delay="default"
    controlOptions={{
      type: "TooltipControlDefault",
      trigger: "mouseenter",
      hideOnClick: true,
    }}
    placement="bottom"
    description={{
      type: "DescriptionContent",
      text: `${props.relatedIssuerName} is a related issuer but does not currently have a public BondLink website.`,
    }}
  >
    {props.children}
  </Tooltip>;
